import React, { useEffect, useRef, useMemo } from 'react';
import { Box, TextField, Typography, InputAdornment } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { UPIPaymentData, UPIProvider } from '../../collectPayment.types';
import { UPI_PROVIDERS } from '../../collectPayment.constants';
import useCurrency from '../../../../../components/CurrencyProvider';

interface UPIPaymentFormProps {
  data: UPIPaymentData;
  onChange: (data: UPIPaymentData) => void;
  errors?: {
    amount?: string;
    provider?: string;
    transactionNumber?: string;
  };
  remainingAmount?: number;
  /** UPI providers from billing-config-options API. Overrides hardcoded list when provided. */
  upiProviders?: { id: number; name: string; code: string }[];
}

/** Map API code to display metadata. Falls back to generic styling for unknown codes. */
const PROVIDER_META: Record<string, { icon: string; color: string }> = {
  PHONEPE: { icon: 'https://upload.wikimedia.org/wikipedia/commons/7/71/PhonePe_Logo.svg', color: '#5f259f' },
  GOOGLE_PAY: { icon: 'https://upload.wikimedia.org/wikipedia/commons/f/f2/Google_Pay_Logo.svg', color: '#4285f4' },
  PAYTM: { icon: 'https://upload.wikimedia.org/wikipedia/commons/2/24/Paytm_Logo_%28standalone%29.svg', color: '#00b9f5' },
};

/** Map backend API codes to UPIProvider union values used in the UI. */
const API_CODE_TO_UPI_PROVIDER: Record<string, UPIProvider> = {
  PHONEPE: 'phonepe',
  GOOGLE_PAY: 'googlepay',
  PAYTM: 'paytm',
};

const DEFAULT_COLOR = '#5A8AEA';

const UPIPaymentForm: React.FC<UPIPaymentFormProps> = ({
  data,
  onChange,
  errors,
  remainingAmount,
  upiProviders,
}) => {
  const { currencySymbol } = useCurrency();
  const prevRemainingRef = useRef(remainingAmount);
  const onChangeRef = useRef(onChange);

  // Keep refs updated with latest values
  onChangeRef.current = onChange;

  // Auto-populate amount with remaining amount when:
  // 1. Form is first rendered with no amount, OR
  // 2. Remaining amount changes (after adding a payment) and current amount is 0
  useEffect(() => {
    const remainingChanged = prevRemainingRef.current !== remainingAmount;
    prevRemainingRef.current = remainingAmount;
    
    if (remainingAmount && remainingAmount > 0 && !data.amount) {
      onChangeRef.current({ ...data, amount: remainingAmount });
    } else if (remainingChanged && remainingAmount && remainingAmount > 0 && data.amount === 0) {
      onChangeRef.current({ ...data, amount: remainingAmount });
    }
  }, [remainingAmount, data]);

  const handleProviderSelect = (provider: UPIProvider | string) => {
    onChange({ ...data, provider: provider as UPIProvider });
  };

  // Build provider list: use API data when available, else fallback to hardcoded
  const providerList = useMemo(() => {
    if (upiProviders && upiProviders.length > 0) {
      return upiProviders.map((p) => {
        const meta = PROVIDER_META[p.code.toUpperCase()] ?? { icon: '', color: DEFAULT_COLOR };
        const normalizedValue = API_CODE_TO_UPI_PROVIDER[p.code.toUpperCase()] ?? p.code.toLowerCase();
        return {
          value: normalizedValue,
          label: p.name,
          icon: meta.icon,
          color: meta.color,
        };
      });
    }
    return UPI_PROVIDERS;
  }, [upiProviders]);

  // Real-time validation: check if amount exceeds remaining
  const amountError = useMemo(() => {
    if (errors?.amount) return errors.amount;
    if (remainingAmount !== undefined && data.amount > remainingAmount) {
      return 'Amount cannot exceed remaining balance';
    }
    return undefined;
  }, [errors?.amount, data.amount, remainingAmount]);

  return (
    <Box>
      <Grid2 container spacing={2} sx={{ mb: 2 }}>
        {providerList.map((provider) => {
          const isSelected = data.provider === provider.value;
          return (
            <Grid2 size={{ xs: 4 }} key={provider.value}>
              <Box
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                aria-label={`Select ${provider.label} as payment provider`}
                onClick={() => handleProviderSelect(provider.value)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleProviderSelect(provider.value);
                  }
                }}
                sx={{
                  py: 2,
                  px: 1,
                  border: isSelected ? `2px solid ${provider.color}` : '1px solid #e0e0e0',
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: provider.color,
                  },
                  '&:focus': {
                    outline: `2px solid ${provider.color}`,
                    outlineOffset: '2px',
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${provider.color}`,
                    outlineOffset: '2px',
                  },
                }}
              >
                <Box
                  component="img"
                  src={provider.icon}
                  alt={provider.label}
                  sx={{
                    height: 28,
                    maxWidth: 80,
                    objectFit: 'contain',
                  }}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 500,
                    color: '#333',
                    fontSize: '12px',
                    mt: 0.5,
                  }}
                >
                  {provider.label}
                </Typography>
              </Box>
            </Grid2>
          );
        })}
      </Grid2>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Amount"
          type="number"
          value={data.amount || ''}
          onChange={(e) => {
            const value = parseFloat(e.target.value) || 0;
            onChange({ ...data, amount: value });
          }}
          placeholder="0.00"
          error={!!amountError}
          helperText={amountError}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  {currencySymbol}
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Transaction Number"
          value={data.transactionNumber || ''}
          onChange={(e) => onChange({ ...data, transactionNumber: e.target.value })}
          placeholder="Enter Transaction Number"
          error={!!errors?.transactionNumber}
          helperText={errors?.transactionNumber}
        />
      </Box>
    </Box>
  );
};

export default UPIPaymentForm;
